import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, API } from "../api";
import { useAuth } from "../AuthContext";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Car, Plus, Fuel, Wrench, Droplet, AlertTriangle, Key, Navigation,
  Calendar, Trash2, Upload, MessageCircle, ChevronLeft, Pencil, LogOut, LogIn,
  BarChart3, ShieldAlert, Download,
} from "lucide-react";
import { toast } from "sonner";

const POLL_MS = 30000;

const STATUS_TONE = {
  active: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  maintenance: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  sold: "bg-slate-500/15 text-slate-400 border-slate-500/30",
  archived: "bg-slate-700/30 text-slate-500 border-slate-700",
};

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString([], { day: "2-digit", month: "short", year: "numeric" });
  } catch { return "—"; }
}

function daysUntil(iso) {
  if (!iso) return null;
  const d = new Date(iso + "T00:00:00");
  const now = new Date(); now.setHours(0, 0, 0, 0);
  return Math.round((d - now) / 86400000);
}

function fmtMoney(v) {
  if (v == null || v === "") return "—";
  return `RM ${Number(v).toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ─── PAGE ────────────────────────────────────────────────────────────────────

export default function Vehicles() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [vehicles, setVehicles] = useState([]);
  const [tab, setTab] = useState("all");
  const [selected, setSelected] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [staff, setStaff] = useState([]);

  const load = useCallback(async () => {
    try {
      const [vl, st] = await Promise.all([
        api.get("/vehicles"),
        api.get("/staff"),
      ]);
      setVehicles(vl.data || []);
      setStaff((st.data || []).filter((u) => u.is_active !== false));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  const filtered = useMemo(() => {
    if (tab === "all") return vehicles.filter((v) => v.status !== "archived");
    if (tab === "attention") return vehicles.filter((v) => v.status !== "archived" && (v.overdue_count > 0 || v.upcoming_count > 0));
    if (tab === "archived") return vehicles.filter((v) => v.status === "archived");
    return vehicles.filter((v) => v.status === tab);
  }, [vehicles, tab]);

  const counts = useMemo(() => {
    const c = { all: 0, attention: 0, active: 0, maintenance: 0, archived: 0 };
    vehicles.forEach((v) => {
      if (v.status !== "archived") c.all++;
      if (v.status === "active") c.active++;
      if (v.status === "maintenance") c.maintenance++;
      if (v.status === "archived") c.archived++;
      if (v.status !== "archived" && (v.overdue_count > 0 || v.upcoming_count > 0)) c.attention++;
    });
    return c;
  }, [vehicles]);

  return (
    <Layout title="Vehicles">
      <div className="space-y-4" data-testid="vehicles-page">
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-xl font-semibold tracking-tight flex items-center gap-2">
            <Car className="w-5 h-5 text-teal-400" /> Vehicles
          </h1>
          <div className="flex-1" />
          {isAdmin && (
            <Button className="bg-teal-600 hover:bg-teal-500 h-9"
              onClick={() => setAddOpen(true)} data-testid="vehicle-add-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Add vehicle
            </Button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 overflow-x-auto scrollbar-thin">
          {[
            ["all", "All"],
            ["attention", "⚠ Attention"],
            ["active", "Active"],
            ["maintenance", "Maintenance"],
            ["archived", "Archived"],
          ].map(([k, l]) => {
            const active = tab === k;
            return (
              <button key={k} onClick={() => setTab(k)}
                data-testid={`vehicle-tab-${k}`}
                className={`text-xs px-3 py-1.5 rounded-full border whitespace-nowrap transition ${active
                  ? "bg-teal-500/15 border-teal-500/60 text-teal-200"
                  : "border-slate-800 hover:border-slate-600 surface-1 text-slate-300"}`}>
                {l}
                <span className="ml-1.5 num text-[10px] opacity-80">{counts[k] || 0}</span>
              </button>
            );
          })}
        </div>

        {filtered.length === 0 && (
          <div className="rounded-lg border hairline surface-1 p-10 text-center">
            <Car className="w-8 h-8 text-slate-600 mx-auto mb-2" />
            <div className="text-sm text-slate-300">No vehicles in this view.</div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="vehicle-grid">
          {filtered.map((v) => (
            <VehicleCard key={v.id} v={v} onOpen={() => setSelected(v)} />
          ))}
        </div>
      </div>

      {addOpen && (
        <EditVehicleDialog vehicle={null} staff={staff}
          onOpenChange={(o) => setAddOpen(o)}
          onDone={() => { load(); setAddOpen(false); }} />
      )}
      {selected && (
        <VehicleDetailDialog
          vehicleId={selected.id}
          isAdmin={isAdmin}
          user={user}
          staff={staff}
          onOpenChange={(v) => !v && setSelected(null)}
          onRefresh={load}
        />
      )}
    </Layout>
  );
}

// ─── CARD ────────────────────────────────────────────────────────────────────

function VehicleCard({ v, onOpen }) {
  const attentionPills = (v.attention_flags || []).slice(0, 3);
  return (
    <div onClick={onOpen}
      data-testid={`vehicle-card-${v.id}`}
      className="rounded-lg border hairline surface-1 p-3 cursor-pointer hover:border-teal-500/40 transition space-y-2">
      <div className="flex items-center gap-2">
        <div className="w-12 h-12 rounded-md bg-slate-800 flex items-center justify-center overflow-hidden shrink-0">
          {v.photo_b64 ? (
            <img src={v.photo_b64} alt={v.plate} className="w-full h-full object-cover" />
          ) : <Car className="w-6 h-6 text-slate-500" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold num uppercase tracking-wider truncate">{v.plate}</div>
          <div className="text-[11px] text-slate-500 truncate">{v.make || ""} {v.model || ""}{v.year ? ` · ${v.year}` : ""}</div>
        </div>
        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border num ${STATUS_TONE[v.status] || STATUS_TONE.active}`}>
          {v.status}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2 text-[11px]">
        <div>
          <div className="text-slate-500">Odometer</div>
          <div className="num text-slate-200">{v.current_odometer ? `${Number(v.current_odometer).toLocaleString()} km` : "—"}</div>
        </div>
        <div>
          <div className="text-slate-500">Current driver</div>
          <div className="text-slate-200 truncate flex items-center gap-1.5">
            {v.current_driver ? (<>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: v.current_driver.color || "#64748b" }} />
              {v.current_driver.display_name}
            </>) : <span className="text-slate-500">— Keys available</span>}
          </div>
        </div>
      </div>

      {attentionPills.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {attentionPills.map((f) => {
            const due = daysUntil(f.date);
            const cls = f.overdue
              ? "bg-red-500/15 text-red-300 border-red-500/40"
              : "bg-amber-500/15 text-amber-300 border-amber-500/40";
            return (
              <span key={f.kind} className={`text-[10px] px-1.5 py-0.5 rounded border num ${cls}`}>
                {f.label}: {f.overdue ? `${-due}d overdue` : `in ${due}d`}
              </span>
            );
          })}
          {(v.attention_flags || []).length > 3 && (
            <span className="text-[10px] text-slate-400">+{(v.attention_flags || []).length - 3} more</span>
          )}
        </div>
      )}

      {(v.assigned_drivers || []).length > 0 && (
        <div className="flex flex-wrap gap-1 pt-1">
          {v.assigned_drivers.slice(0, 4).map((d) => (
            <span key={d.id} className="text-[10px] px-1.5 py-0.5 rounded-full border hairline surface-2 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: d.color || "#64748b" }} />
              {d.display_name}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── DETAIL DIALOG ───────────────────────────────────────────────────────────

function VehicleDetailDialog({ vehicleId, isAdmin, user, staff, onOpenChange, onRefresh }) {
  const [v, setV] = useState(null);
  const [subTab, setSubTab] = useState("overview");
  const [fuels, setFuels] = useState([]);
  const [trips, setTrips] = useState([]);
  const [services, setServices] = useState([]);
  const [washes, setWashes] = useState([]);
  const [damages, setDamages] = useState([]);
  const [checkouts, setCheckouts] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [addType, setAddType] = useState(null); // "fuel"|"trip"|"service"|"wash"|"damage"|null
  const [deleteOpen, setDeleteOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get(`/vehicles/${vehicleId}`);
      setV(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
      onOpenChange(false);
    }
  }, [vehicleId, onOpenChange]);

  useEffect(() => { refresh(); }, [refresh]);

  // Load subtab data on demand
  useEffect(() => {
    if (!v) return;
    const paths = {
      fuel: "fuel", trips: "trips", service: "service",
      wash: "wash", damage: "damage", checkouts: "checkouts",
    };
    if (!paths[subTab]) return;
    api.get(`/vehicles/${vehicleId}/${paths[subTab]}`).then(({ data }) => {
      if (subTab === "fuel") setFuels(data);
      if (subTab === "trips") setTrips(data);
      if (subTab === "service") setServices(data);
      if (subTab === "wash") setWashes(data);
      if (subTab === "damage") setDamages(data);
      if (subTab === "checkouts") setCheckouts(data);
    }).catch(() => {});
  }, [subTab, v, vehicleId]);

  const doCheckout = async () => {
    try {
      await api.post(`/vehicles/${vehicleId}/checkout`, { purpose: "", start_km: v?.current_odometer || null });
      toast.success("Keys checked out to you");
      refresh(); onRefresh?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const doCheckin = async () => {
    const endKm = prompt("Ending odometer (km)?", String(v?.current_odometer || ""));
    const body = endKm && !isNaN(Number(endKm)) ? { end_km: Number(endKm) } : {};
    try {
      await api.post(`/vehicles/${vehicleId}/checkin`, body);
      toast.success("Checked in");
      refresh(); onRefresh?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const deleteVehicle = async () => {
    try {
      await api.delete(`/vehicles/${vehicleId}`);
      toast.success("Vehicle deleted");
      setDeleteOpen(false);
      onRefresh?.();
      onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!v) {
    return (
      <Dialog open onOpenChange={onOpenChange}>
        <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-3xl">
          <DialogTitle>Loading vehicle…</DialogTitle>
        </DialogContent>
      </Dialog>
    );
  }

  const isCurrentDriver = v.current_driver?.id === user?.id;

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-4xl max-h-[90vh] flex flex-col p-0">
        <DialogHeader className="p-4 border-b hairline">
          <DialogTitle className="flex items-center gap-3">
            <span className="w-8 h-8 rounded bg-slate-800 flex items-center justify-center overflow-hidden shrink-0">
              {v.photo_b64 ? <img src={v.photo_b64} alt={v.plate} className="w-full h-full object-cover" /> : <Car className="w-4 h-4 text-slate-500" />}
            </span>
            <span className="num uppercase tracking-wider">{v.plate}</span>
            <span className="text-xs text-slate-400 font-normal">{v.make} {v.model}{v.year ? ` · ${v.year}` : ""}</span>
            <span className={`ml-auto text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border num ${STATUS_TONE[v.status]}`}>{v.status}</span>
          </DialogTitle>
        </DialogHeader>

        {/* Sub-tabs */}
        <div className="flex items-center gap-1 border-b hairline px-2 overflow-x-auto scrollbar-thin">
          {[
            ["overview", "Overview"],
            ["fuel", "Fuel"],
            ["trips", "Trips"],
            ["service", "Service"],
            ["wash", "Wash"],
            ["damage", "Damage"],
            ["checkouts", "Key log"],
          ].map(([k, l]) => {
            const active = subTab === k;
            return (
              <button key={k} onClick={() => setSubTab(k)}
                data-testid={`vehicle-subtab-${k}`}
                className={`text-xs px-3 py-2 whitespace-nowrap border-b-2 transition ${active
                  ? "border-teal-500 text-teal-200"
                  : "border-transparent text-slate-400 hover:text-slate-200"}`}>
                {l}
              </button>
            );
          })}
          <div className="flex-1" />
          <div className="flex items-center gap-1 py-1">
            {v.current_driver ? (
              <Button size="sm" variant="outline"
                disabled={!isCurrentDriver && !isAdmin}
                className="h-7 text-xs border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                onClick={doCheckin} data-testid="vehicle-checkin">
                <LogIn className="w-3 h-3 mr-1" /> Check in
              </Button>
            ) : (
              <Button size="sm" variant="outline"
                className="h-7 text-xs border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                onClick={doCheckout} data-testid="vehicle-checkout">
                <Key className="w-3 h-3 mr-1" /> Take keys
              </Button>
            )}
            {isAdmin && (
              <Button size="sm" variant="outline"
                className="h-7 text-xs border-slate-700"
                onClick={() => setEditOpen(true)} data-testid="vehicle-edit">
                <Pencil className="w-3 h-3 mr-1" /> Edit
              </Button>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 text-sm">
          {subTab === "overview" && <VehicleOverview v={v} />}
          {subTab === "fuel" && <FuelList vehicleId={vehicleId} rows={fuels}
            onAdd={() => setAddType("fuel")}
            onDelete={async (id) => { await api.delete(`/vehicles/${vehicleId}/fuel/${id}`); setFuels(fuels.filter((r) => r.id !== id)); toast.success("Deleted"); refresh(); }}
            isAdmin={isAdmin} />}
          {subTab === "trips" && <TripsList rows={trips}
            onAdd={() => setAddType("trip")}
            onDelete={async (id) => { await api.delete(`/vehicles/${vehicleId}/trips/${id}`); setTrips(trips.filter((r) => r.id !== id)); toast.success("Deleted"); }}
            isAdmin={isAdmin} />}
          {subTab === "service" && <ServiceList vehicleId={vehicleId} rows={services}
            onAdd={() => setAddType("service")}
            onDelete={async (id) => { await api.delete(`/vehicles/${vehicleId}/service/${id}`); setServices(services.filter((r) => r.id !== id)); toast.success("Deleted"); refresh(); }}
            isAdmin={isAdmin} />}
          {subTab === "wash" && <WashList rows={washes}
            onAdd={() => setAddType("wash")}
            onDelete={async (id) => { await api.delete(`/vehicles/${vehicleId}/wash/${id}`); setWashes(washes.filter((r) => r.id !== id)); toast.success("Deleted"); refresh(); }}
            isAdmin={isAdmin} />}
          {subTab === "damage" && <DamageList vehicleId={vehicleId} rows={damages}
            onAdd={() => setAddType("damage")}
            onDelete={async (id) => { await api.delete(`/vehicles/${vehicleId}/damage/${id}`); setDamages(damages.filter((r) => r.id !== id)); toast.success("Deleted"); }}
            isAdmin={isAdmin} />}
          {subTab === "checkouts" && <CheckoutsList rows={checkouts} />}
        </div>

        {/* Danger zone */}
        {isAdmin && subTab === "overview" && (
          <div className="px-4 py-3 border-t hairline flex items-center justify-end">
            <Button variant="outline"
              onClick={() => setDeleteOpen(true)}
              className="h-8 text-xs border-red-500/40 text-red-300 hover:bg-red-500/10 hover:text-red-200"
              data-testid="vehicle-delete-btn">
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete vehicle
            </Button>
          </div>
        )}
      </DialogContent>

      {editOpen && (
        <EditVehicleDialog vehicle={v} staff={staff}
          onOpenChange={setEditOpen}
          onDone={() => { setEditOpen(false); refresh(); onRefresh?.(); }} />
      )}

      {addType === "fuel" && (
        <AddFuelDialog vehicleId={vehicleId} onClose={() => setAddType(null)}
          onSaved={() => { setAddType(null); setSubTab("fuel"); refresh(); onRefresh?.();
            api.get(`/vehicles/${vehicleId}/fuel`).then(({ data }) => setFuels(data)); }} />
      )}
      {addType === "trip" && (
        <AddTripDialog vehicleId={vehicleId} currentKm={v.current_odometer} staff={staff}
          onClose={() => setAddType(null)}
          onSaved={() => { setAddType(null); setSubTab("trips"); refresh();
            api.get(`/vehicles/${vehicleId}/trips`).then(({ data }) => setTrips(data)); }} />
      )}
      {addType === "service" && (
        <AddServiceDialog vehicleId={vehicleId} currentKm={v.current_odometer}
          onClose={() => setAddType(null)}
          onSaved={() => { setAddType(null); setSubTab("service"); refresh(); onRefresh?.();
            api.get(`/vehicles/${vehicleId}/service`).then(({ data }) => setServices(data)); }} />
      )}
      {addType === "wash" && (
        <AddWashDialog vehicleId={vehicleId}
          onClose={() => setAddType(null)}
          onSaved={() => { setAddType(null); setSubTab("wash"); refresh(); onRefresh?.();
            api.get(`/vehicles/${vehicleId}/wash`).then(({ data }) => setWashes(data)); }} />
      )}
      {addType === "damage" && (
        <AddDamageDialog vehicleId={vehicleId}
          onClose={() => setAddType(null)}
          onSaved={() => { setAddType(null); setSubTab("damage"); refresh();
            api.get(`/vehicles/${vehicleId}/damage`).then(({ data }) => setDamages(data)); }} />
      )}

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent className="bg-[#0f1626] border-slate-800 text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete vehicle {v.plate}?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              This permanently removes the vehicle and ALL its logs (fuel / trips / service / wash / damage / checkouts). No undo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-slate-700">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteVehicle} className="bg-red-600 hover:bg-red-500"
              data-testid="vehicle-delete-confirm">Delete permanently</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}

// ─── OVERVIEW TAB ────────────────────────────────────────────────────────────

function VehicleOverview({ v }) {
  return (
    <div className="space-y-3">
      {(v.attention_flags || []).length > 0 && (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3">
          <div className="text-xs uppercase tracking-wider text-amber-300 mb-1.5 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" /> Attention items
          </div>
          <div className="flex flex-wrap gap-1.5">
            {v.attention_flags.map((f) => {
              const due = daysUntil(f.date);
              const cls = f.overdue
                ? "bg-red-500/20 text-red-200 border-red-500/40"
                : "bg-amber-500/20 text-amber-200 border-amber-500/40";
              return (
                <span key={f.kind} className={`text-xs px-2 py-1 rounded border num ${cls}`}>
                  {f.label} · {fmtDate(f.date)} · {f.overdue ? `${-due}d overdue` : `in ${due}d`}
                </span>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Stat label="Odometer" value={v.current_odometer ? `${Number(v.current_odometer).toLocaleString()} km` : "—"} />
        <Stat label="Type" value={v.vehicle_type} />
        <Stat label="Color" value={v.color || "—"} />
        <Stat label="Road tax" value={fmtDate(v.road_tax_expiry)} />
        <Stat label="Insurance" value={fmtDate(v.insurance_expiry)} />
        <Stat label="Puspakom" value={fmtDate(v.puspakom_next)} />
        <Stat label="Last service" value={fmtDate(v.last_service_date) + (v.last_service_km ? ` · ${Number(v.last_service_km).toLocaleString()} km` : "")} />
        <Stat label="Next service" value={fmtDate(v.next_service_date) + (v.next_service_km ? ` · ${Number(v.next_service_km).toLocaleString()} km` : "")} />
        <Stat label="Last wash" value={fmtDate(v.last_wash_date)} />
        <Stat label="Next wash" value={fmtDate(v.next_wash_date)} />
        <Stat label="Current driver" value={v.current_driver ? v.current_driver.display_name : "— Keys available"} />
      </div>

      {v.notes && (
        <div className="rounded-md border hairline surface-2 p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 num mb-1">Notes</div>
          <div className="text-sm whitespace-pre-wrap">{v.notes}</div>
        </div>
      )}

      {(v.assigned_drivers || []).length > 0 && (
        <div className="rounded-md border hairline surface-2 p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 num mb-2">Authorised drivers</div>
          <div className="flex flex-wrap gap-1.5">
            {v.assigned_drivers.map((d) => (
              <span key={d.id} className="text-xs px-2 py-0.5 rounded-full border hairline surface-2 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: d.color || "#64748b" }} />
                {d.display_name}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-md border hairline surface-2 p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 num">{label}</div>
      <div className="text-sm mt-0.5 text-slate-200 break-words">{value || "—"}</div>
    </div>
  );
}

// ─── LISTS ───────────────────────────────────────────────────────────────────

function SubHeader({ title, onAdd }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <div className="text-xs uppercase tracking-wider text-slate-500 num">{title}</div>
      <div className="flex-1" />
      <Button size="sm" variant="outline" onClick={onAdd}
        className="h-7 text-xs border-teal-500/50 text-teal-300 hover:bg-teal-500/10"
        data-testid={`vehicle-add-${title.toLowerCase()}-btn`}>
        <Plus className="w-3 h-3 mr-1" /> Add {title.toLowerCase()}
      </Button>
    </div>
  );
}

function EmptyRow({ icon: Icon, label }) {
  return (
    <div className="rounded-md border hairline surface-2 p-6 text-center text-xs text-slate-500">
      <Icon className="w-5 h-5 text-slate-600 mx-auto mb-1.5" /> No {label} yet.
    </div>
  );
}

function FuelList({ vehicleId, rows, onAdd, onDelete, isAdmin }) {
  return (
    <div>
      <SubHeader title="Fuel" onAdd={onAdd} />
      {rows.length === 0 ? <EmptyRow icon={Fuel} label="fuel logs" /> : (
        <div className="space-y-1.5">
          {rows.map((r) => (
            <div key={r.id} className="rounded-md border hairline surface-2 p-3 grid grid-cols-[1fr_auto] gap-2">
              <div className="min-w-0">
                <div className="text-xs num text-slate-200">
                  {fmtDate(r.date)} · <span className="text-emerald-300">{fmtMoney(r.cost_rm)}</span> · {r.liters} L
                  {r.station && <span className="text-slate-400"> · {r.station}</span>}
                </div>
                <div className="text-[11px] text-slate-500 num">
                  {r.odometer ? `@${Number(r.odometer).toLocaleString()} km` : ""}
                  {r.km_per_liter ? ` · ${r.km_per_liter} km/L` : ""}
                  {r.km_since_last ? ` · ${r.km_since_last} km since last fill` : ""}
                  {r.by_user_name ? ` · by ${r.by_user_name}` : ""}
                </div>
              </div>
              {isAdmin && (
                <Button size="icon" variant="ghost" onClick={() => onDelete(r.id)}
                  className="h-7 w-7 text-slate-500 hover:text-red-400">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TripsList({ rows, onAdd, onDelete, isAdmin }) {
  return (
    <div>
      <SubHeader title="Trips" onAdd={onAdd} />
      {rows.length === 0 ? <EmptyRow icon={Navigation} label="trips" /> : (
        <div className="space-y-1.5">
          {rows.map((r) => (
            <div key={r.id} className="rounded-md border hairline surface-2 p-3 grid grid-cols-[1fr_auto] gap-2">
              <div className="min-w-0">
                <div className="text-xs text-slate-200">
                  {fmtDate(r.date)} · <b>{r.from_location || "—"}</b> → <b>{r.to_location || "—"}</b>
                  {r.km != null && <span className="num text-teal-300"> · {r.km} km</span>}
                </div>
                <div className="text-[11px] text-slate-500">
                  {r.purpose ? `${r.purpose} · ` : ""}
                  by {r.driver_name}
                  {r.start_km != null && r.end_km != null ? ` · ${r.start_km}→${r.end_km}` : ""}
                </div>
              </div>
              {isAdmin && (
                <Button size="icon" variant="ghost" onClick={() => onDelete(r.id)}
                  className="h-7 w-7 text-slate-500 hover:text-red-400">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ServiceList({ rows, onAdd, onDelete, isAdmin }) {
  return (
    <div>
      <SubHeader title="Service" onAdd={onAdd} />
      {rows.length === 0 ? <EmptyRow icon={Wrench} label="service records" /> : (
        <div className="space-y-1.5">
          {rows.map((r) => (
            <div key={r.id} className="rounded-md border hairline surface-2 p-3 grid grid-cols-[1fr_auto] gap-2">
              <div className="min-w-0">
                <div className="text-xs text-slate-200">
                  {fmtDate(r.date)} · <span className="uppercase num text-amber-300">{r.service_type}</span>
                  {r.cost_rm != null && <span className="num text-emerald-300"> · {fmtMoney(r.cost_rm)}</span>}
                  {r.km != null && <span className="num text-slate-400"> · @{Number(r.km).toLocaleString()} km</span>}
                </div>
                {r.notes && <div className="text-[11px] text-slate-500 whitespace-pre-wrap mt-0.5">{r.notes}</div>}
                <div className="text-[11px] text-slate-500">by {r.by_user_name}</div>
              </div>
              {isAdmin && (
                <Button size="icon" variant="ghost" onClick={() => onDelete(r.id)}
                  className="h-7 w-7 text-slate-500 hover:text-red-400">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function WashList({ rows, onAdd, onDelete, isAdmin }) {
  return (
    <div>
      <SubHeader title="Wash" onAdd={onAdd} />
      {rows.length === 0 ? <EmptyRow icon={Droplet} label="wash records" /> : (
        <div className="space-y-1.5">
          {rows.map((r) => (
            <div key={r.id} className="rounded-md border hairline surface-2 p-3 grid grid-cols-[1fr_auto] gap-2">
              <div className="min-w-0">
                <div className="text-xs text-slate-200">
                  {fmtDate(r.date)}
                  {r.location && <span> · {r.location}</span>}
                  {r.cost_rm != null && <span className="num text-emerald-300"> · {fmtMoney(r.cost_rm)}</span>}
                </div>
                <div className="text-[11px] text-slate-500">by {r.by_user_name}{r.notes ? ` · ${r.notes}` : ""}</div>
              </div>
              {isAdmin && (
                <Button size="icon" variant="ghost" onClick={() => onDelete(r.id)}
                  className="h-7 w-7 text-slate-500 hover:text-red-400">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DamageList({ rows, onAdd, onDelete, isAdmin }) {
  return (
    <div>
      <SubHeader title="Damage" onAdd={onAdd} />
      {rows.length === 0 ? <EmptyRow icon={ShieldAlert} label="damage / summons records" /> : (
        <div className="space-y-1.5">
          {rows.map((r) => (
            <div key={r.id} className="rounded-md border hairline surface-2 p-3 grid grid-cols-[1fr_auto] gap-2">
              <div className="min-w-0">
                <div className="text-xs text-slate-200">
                  {fmtDate(r.date)} · <span className="uppercase num text-red-300">{r.damage_type}</span>
                  {r.cost_rm != null && <span className="num text-red-300"> · {fmtMoney(r.cost_rm)}</span>}
                </div>
                {r.description && <div className="text-[11px] text-slate-500 whitespace-pre-wrap mt-0.5">{r.description}</div>}
                <div className="text-[11px] text-slate-500">by {r.by_user_name}</div>
              </div>
              {isAdmin && (
                <Button size="icon" variant="ghost" onClick={() => onDelete(r.id)}
                  className="h-7 w-7 text-slate-500 hover:text-red-400">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CheckoutsList({ rows }) {
  return (
    <div>
      <SubHeader title="Key log" onAdd={() => toast.info("Use 'Take keys' / 'Check in' above")} />
      {rows.length === 0 ? <EmptyRow icon={Key} label="key checkouts" /> : (
        <div className="space-y-1.5">
          {rows.map((r) => (
            <div key={r.id} className="rounded-md border hairline surface-2 p-3 text-xs">
              <div className="text-slate-200">
                <b>{r.user_name}</b> · {fmtDate(r.checked_out_at)} {r.checked_in_at ? `→ ${fmtDate(r.checked_in_at)}` : "(still out)"}
              </div>
              <div className="text-[11px] text-slate-500">
                {r.purpose ? `${r.purpose} · ` : ""}
                {r.start_km != null ? `${r.start_km}` : "—"}
                {r.end_km != null ? ` → ${r.end_km}` : ""}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── ADD DIALOGS ─────────────────────────────────────────────────────────────

function useFileReader() {
  return (file, setB64) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Max 10 MB"); return; }
    const r = new FileReader();
    r.onload = (e) => setB64(String(e.target.result));
    r.readAsDataURL(file);
  };
}

function AddFuelDialog({ vehicleId, onClose, onSaved }) {
  const [f, setF] = useState({ date: todayIso(), liters: "", cost_rm: "", station: "", odometer: "", notes: "" });
  const [receipt, setReceipt] = useState("");
  const [busy, setBusy] = useState(false);
  const pick = useFileReader();
  const save = async () => {
    if (!f.liters || !f.cost_rm) { toast.error("Liters + cost required"); return; }
    setBusy(true);
    try {
      await api.post(`/vehicles/${vehicleId}/fuel`, {
        date: f.date, liters: Number(f.liters), cost_rm: Number(f.cost_rm),
        station: f.station, odometer: f.odometer === "" ? null : Number(f.odometer),
        notes: f.notes, receipt_b64: receipt,
      });
      toast.success("Fuel logged");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-md">
        <DialogHeader><DialogTitle>Log fuel</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <LabeledInput l="Date" type="date" v={f.date} on={(e) => setF({ ...f, date: e.target.value })} tid="fuel-date" />
          <LabeledInput l="Odometer" type="number" v={f.odometer} on={(e) => setF({ ...f, odometer: e.target.value })} tid="fuel-odometer" />
          <LabeledInput l="Liters" type="number" step="0.01" v={f.liters} on={(e) => setF({ ...f, liters: e.target.value })} tid="fuel-liters" />
          <LabeledInput l="Cost (RM)" type="number" step="0.01" v={f.cost_rm} on={(e) => setF({ ...f, cost_rm: e.target.value })} tid="fuel-cost" />
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 num">Station</Label>
            <Input value={f.station} onChange={(e) => setF({ ...f, station: e.target.value })}
              className="bg-[#0a0e1a] border-slate-800 mt-1" data-testid="fuel-station" />
          </div>
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 num">Receipt (optional)</Label>
            <input type="file" accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => pick(e.target.files?.[0], setReceipt)}
              className="block w-full mt-1 text-xs text-slate-300 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-slate-700 file:text-white"
              data-testid="fuel-receipt" />
            {receipt && <div className="text-[10px] text-slate-500 mt-1">Attached ✓</div>}
          </div>
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 num">Notes</Label>
            <Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })}
              className="bg-[#0a0e1a] border-slate-800 mt-1 min-h-[40px]" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-teal-600 hover:bg-teal-500"
            data-testid="fuel-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddTripDialog({ vehicleId, currentKm, staff, onClose, onSaved }) {
  const [f, setF] = useState({
    date: todayIso(), from_location: "", to_location: "", purpose: "",
    start_km: currentKm ? String(currentKm) : "", end_km: "", driver_user_id: "", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/vehicles/${vehicleId}/trips`, {
        date: f.date, from_location: f.from_location, to_location: f.to_location,
        purpose: f.purpose,
        start_km: f.start_km === "" ? null : Number(f.start_km),
        end_km: f.end_km === "" ? null : Number(f.end_km),
        driver_user_id: f.driver_user_id || null,
        notes: f.notes,
      });
      toast.success("Trip logged"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-md">
        <DialogHeader><DialogTitle>Log trip</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <LabeledInput l="Date" type="date" v={f.date} on={(e) => setF({ ...f, date: e.target.value })} tid="trip-date" />
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 num">Driver</Label>
            <Select value={f.driver_user_id || "__me"} onValueChange={(v) => setF({ ...f, driver_user_id: v === "__me" ? "" : v })}>
              <SelectTrigger className="bg-[#0a0e1a] border-slate-800 mt-1" data-testid="trip-driver">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__me">Me</SelectItem>
                {(staff || []).map((u) => <SelectItem key={u.id} value={u.id}>{u.display_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <LabeledInput l="From" v={f.from_location} on={(e) => setF({ ...f, from_location: e.target.value })} tid="trip-from" />
          <LabeledInput l="To" v={f.to_location} on={(e) => setF({ ...f, to_location: e.target.value })} tid="trip-to" />
          <LabeledInput l="Start km" type="number" v={f.start_km} on={(e) => setF({ ...f, start_km: e.target.value })} tid="trip-start" />
          <LabeledInput l="End km" type="number" v={f.end_km} on={(e) => setF({ ...f, end_km: e.target.value })} tid="trip-end" />
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 num">Purpose</Label>
            <Input value={f.purpose} onChange={(e) => setF({ ...f, purpose: e.target.value })}
              className="bg-[#0a0e1a] border-slate-800 mt-1" data-testid="trip-purpose" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-teal-600 hover:bg-teal-500" data-testid="trip-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddServiceDialog({ vehicleId, currentKm, onClose, onSaved }) {
  const [f, setF] = useState({
    date: todayIso(), service_type: "service", km: currentKm ? String(currentKm) : "",
    cost_rm: "", notes: "", next_service_date: "", next_service_km: "",
  });
  const [invoice, setInvoice] = useState("");
  const [busy, setBusy] = useState(false);
  const pick = useFileReader();
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/vehicles/${vehicleId}/service`, {
        date: f.date, service_type: f.service_type,
        km: f.km === "" ? null : Number(f.km),
        cost_rm: f.cost_rm === "" ? 0 : Number(f.cost_rm),
        notes: f.notes, invoice_b64: invoice,
        next_service_date: f.next_service_date || null,
        next_service_km: f.next_service_km === "" ? null : Number(f.next_service_km),
      });
      toast.success("Service logged"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-md">
        <DialogHeader><DialogTitle>Log service</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <LabeledInput l="Date" type="date" v={f.date} on={(e) => setF({ ...f, date: e.target.value })} tid="svc-date" />
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 num">Type</Label>
            <Select value={f.service_type} onValueChange={(v) => setF({ ...f, service_type: v })}>
              <SelectTrigger className="bg-[#0a0e1a] border-slate-800 mt-1" data-testid="svc-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["service", "tire", "battery", "inspection", "repair", "other"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <LabeledInput l="Odometer (km)" type="number" v={f.km} on={(e) => setF({ ...f, km: e.target.value })} tid="svc-km" />
          <LabeledInput l="Cost (RM)" type="number" step="0.01" v={f.cost_rm} on={(e) => setF({ ...f, cost_rm: e.target.value })} tid="svc-cost" />
          <LabeledInput l="Next service date" type="date" v={f.next_service_date} on={(e) => setF({ ...f, next_service_date: e.target.value })} tid="svc-next-date" />
          <LabeledInput l="Next service km" type="number" v={f.next_service_km} on={(e) => setF({ ...f, next_service_km: e.target.value })} tid="svc-next-km" />
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 num">Invoice (optional)</Label>
            <input type="file" accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => pick(e.target.files?.[0], setInvoice)}
              className="block w-full mt-1 text-xs text-slate-300 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-slate-700 file:text-white"
              data-testid="svc-invoice" />
            {invoice && <div className="text-[10px] text-slate-500 mt-1">Attached ✓</div>}
          </div>
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 num">Notes</Label>
            <Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })}
              className="bg-[#0a0e1a] border-slate-800 mt-1 min-h-[40px]" placeholder="Oil change, filter, what was done…" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-teal-600 hover:bg-teal-500" data-testid="svc-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddWashDialog({ vehicleId, onClose, onSaved }) {
  const [f, setF] = useState({ date: todayIso(), location: "", cost_rm: "", next_wash_date: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/vehicles/${vehicleId}/wash`, {
        date: f.date, location: f.location,
        cost_rm: f.cost_rm === "" ? 0 : Number(f.cost_rm),
        next_wash_date: f.next_wash_date || null,
        notes: f.notes,
      });
      toast.success("Wash logged"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-md">
        <DialogHeader><DialogTitle>Log wash</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <LabeledInput l="Date" type="date" v={f.date} on={(e) => setF({ ...f, date: e.target.value })} tid="wash-date" />
          <LabeledInput l="Cost (RM)" type="number" step="0.01" v={f.cost_rm} on={(e) => setF({ ...f, cost_rm: e.target.value })} tid="wash-cost" />
          <div className="col-span-2"><LabeledInput l="Location" v={f.location} on={(e) => setF({ ...f, location: e.target.value })} tid="wash-loc" /></div>
          <LabeledInput l="Next wash" type="date" v={f.next_wash_date} on={(e) => setF({ ...f, next_wash_date: e.target.value })} tid="wash-next" />
          <div />
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 num">Notes</Label>
            <Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })}
              className="bg-[#0a0e1a] border-slate-800 mt-1 min-h-[40px]" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-teal-600 hover:bg-teal-500" data-testid="wash-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddDamageDialog({ vehicleId, onClose, onSaved }) {
  const [f, setF] = useState({ date: todayIso(), damage_type: "damage", description: "", cost_rm: "" });
  const [photo, setPhoto] = useState("");
  const [busy, setBusy] = useState(false);
  const pick = useFileReader();
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/vehicles/${vehicleId}/damage`, {
        date: f.date, damage_type: f.damage_type, description: f.description,
        cost_rm: f.cost_rm === "" ? 0 : Number(f.cost_rm),
        photo_b64: photo,
      });
      toast.success("Damage logged"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-md">
        <DialogHeader><DialogTitle>Log damage / summons</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <LabeledInput l="Date" type="date" v={f.date} on={(e) => setF({ ...f, date: e.target.value })} tid="dmg-date" />
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 num">Type</Label>
            <Select value={f.damage_type} onValueChange={(v) => setF({ ...f, damage_type: v })}>
              <SelectTrigger className="bg-[#0a0e1a] border-slate-800 mt-1" data-testid="dmg-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["accident", "summons", "damage", "other"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 num">Description</Label>
            <Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })}
              className="bg-[#0a0e1a] border-slate-800 mt-1 min-h-[60px]" />
          </div>
          <LabeledInput l="Cost (RM)" type="number" step="0.01" v={f.cost_rm} on={(e) => setF({ ...f, cost_rm: e.target.value })} tid="dmg-cost" />
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 num">Photo (optional)</Label>
            <input type="file" accept=".jpg,.jpeg,.png"
              onChange={(e) => pick(e.target.files?.[0], setPhoto)}
              className="block w-full mt-1 text-xs text-slate-300 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-slate-700 file:text-white"
              data-testid="dmg-photo" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-teal-600 hover:bg-teal-500" data-testid="dmg-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── EDIT/CREATE VEHICLE ─────────────────────────────────────────────────────

function EditVehicleDialog({ vehicle, staff, onOpenChange, onDone }) {
  const creating = !vehicle;
  const [f, setF] = useState(() => ({
    plate: vehicle?.plate || "",
    make: vehicle?.make || "",
    model: vehicle?.model || "",
    year: vehicle?.year || "",
    color: vehicle?.color || "",
    vehicle_type: vehicle?.vehicle_type || "car",
    current_odometer: vehicle?.current_odometer ?? "",
    status: vehicle?.status || "active",
    notes: vehicle?.notes || "",
    road_tax_expiry: vehicle?.road_tax_expiry || "",
    insurance_expiry: vehicle?.insurance_expiry || "",
    puspakom_next: vehicle?.puspakom_next || "",
    next_service_date: vehicle?.next_service_date || "",
    next_service_km: vehicle?.next_service_km ?? "",
    next_wash_date: vehicle?.next_wash_date || "",
    last_service_date: vehicle?.last_service_date || "",
    last_service_km: vehicle?.last_service_km ?? "",
    last_wash_date: vehicle?.last_wash_date || "",
    assigned_driver_ids: vehicle?.assigned_driver_ids || [],
  }));
  const [photo, setPhoto] = useState(vehicle?.photo_b64 || "");
  const [busy, setBusy] = useState(false);
  const pick = useFileReader();

  const toggleDriver = (id) => {
    const has = f.assigned_driver_ids.includes(id);
    setF({ ...f, assigned_driver_ids: has
      ? f.assigned_driver_ids.filter((x) => x !== id)
      : [...f.assigned_driver_ids, id] });
  };

  const save = async () => {
    if (!f.plate.trim()) { toast.error("Plate required"); return; }
    setBusy(true);
    try {
      const body = {
        ...f,
        year: f.year === "" ? null : Number(f.year),
        current_odometer: f.current_odometer === "" ? 0 : Number(f.current_odometer),
        next_service_km: f.next_service_km === "" ? null : Number(f.next_service_km),
        last_service_km: f.last_service_km === "" ? null : Number(f.last_service_km),
        photo_b64: photo,
      };
      // Strip empty date strings to null
      ["road_tax_expiry", "insurance_expiry", "puspakom_next", "next_service_date", "next_wash_date", "last_service_date", "last_wash_date"].forEach((k) => { if (body[k] === "") body[k] = null; });
      if (creating) {
        await api.post("/vehicles", body);
        toast.success("Vehicle added");
      } else {
        await api.put(`/vehicles/${vehicle.id}`, body);
        toast.success("Vehicle updated");
      }
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader><DialogTitle>{creating ? "Add vehicle" : "Edit vehicle"}</DialogTitle></DialogHeader>
        <div className="flex-1 overflow-y-auto grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
          <LabeledInput l="Plate *" v={f.plate} on={(e) => setF({ ...f, plate: e.target.value.toUpperCase() })} tid="vh-plate" />
          <LabeledInput l="Make" v={f.make} on={(e) => setF({ ...f, make: e.target.value })} tid="vh-make" />
          <LabeledInput l="Model" v={f.model} on={(e) => setF({ ...f, model: e.target.value })} tid="vh-model" />
          <LabeledInput l="Year" type="number" v={f.year} on={(e) => setF({ ...f, year: e.target.value })} tid="vh-year" />
          <LabeledInput l="Color" v={f.color} on={(e) => setF({ ...f, color: e.target.value })} tid="vh-color" />
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 num">Type</Label>
            <Select value={f.vehicle_type} onValueChange={(v) => setF({ ...f, vehicle_type: v })}>
              <SelectTrigger className="bg-[#0a0e1a] border-slate-800 mt-1" data-testid="vh-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["car", "van", "truck", "motorcycle", "other"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <LabeledInput l="Odometer (km)" type="number" v={f.current_odometer} on={(e) => setF({ ...f, current_odometer: e.target.value })} tid="vh-odo" />
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 num">Status</Label>
            <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
              <SelectTrigger className="bg-[#0a0e1a] border-slate-800 mt-1" data-testid="vh-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["active", "maintenance", "sold", "archived"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div />

          <LabeledInput l="Road tax expiry" type="date" v={f.road_tax_expiry} on={(e) => setF({ ...f, road_tax_expiry: e.target.value })} tid="vh-tax" />
          <LabeledInput l="Insurance expiry" type="date" v={f.insurance_expiry} on={(e) => setF({ ...f, insurance_expiry: e.target.value })} tid="vh-ins" />
          <LabeledInput l="Puspakom next" type="date" v={f.puspakom_next} on={(e) => setF({ ...f, puspakom_next: e.target.value })} tid="vh-pusp" />
          <LabeledInput l="Next service date" type="date" v={f.next_service_date} on={(e) => setF({ ...f, next_service_date: e.target.value })} tid="vh-nsvc-d" />
          <LabeledInput l="Next service km" type="number" v={f.next_service_km} on={(e) => setF({ ...f, next_service_km: e.target.value })} tid="vh-nsvc-k" />
          <LabeledInput l="Next wash date" type="date" v={f.next_wash_date} on={(e) => setF({ ...f, next_wash_date: e.target.value })} tid="vh-nw" />

          <div className="col-span-2 md:col-span-3">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 num mb-1 block">Authorised drivers (multi)</Label>
            <div className="flex flex-wrap gap-1.5">
              {(staff || []).map((u) => {
                const on = f.assigned_driver_ids.includes(u.id);
                return (
                  <button key={u.id} type="button" onClick={() => toggleDriver(u.id)}
                    data-testid={`vh-driver-${u.id}`}
                    className={`text-[11px] px-2 py-0.5 rounded-full border flex items-center gap-1 transition ${on
                      ? "bg-teal-500/20 border-teal-500/60 text-teal-200"
                      : "border-slate-700 hover:border-slate-500"}`}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: u.color || "#64748b" }} />
                    {u.display_name}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="col-span-2 md:col-span-3">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 num">Photo</Label>
            <input type="file" accept=".jpg,.jpeg,.png,.webp"
              onChange={(e) => pick(e.target.files?.[0], setPhoto)}
              className="block w-full mt-1 text-xs text-slate-300 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-slate-700 file:text-white"
              data-testid="vh-photo" />
            {photo && <img src={photo} alt="" className="mt-1 h-16 rounded border hairline" />}
          </div>

          <div className="col-span-2 md:col-span-3">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 num">Notes</Label>
            <Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })}
              className="bg-[#0a0e1a] border-slate-800 mt-1 min-h-[60px]" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-teal-600 hover:bg-teal-500" data-testid="vh-save">
            {creating ? "Create" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LabeledInput({ l, v, on, type, step, tid }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-slate-500 num">{l}</Label>
      <Input value={v} onChange={on} type={type || "text"} step={step}
        className="bg-[#0a0e1a] border-slate-800 mt-1"
        data-testid={tid} />
    </div>
  );
}
